/**
 * Modules whose only output is an outside service that is not connected
 * (MODULE-AUDIT "GATED"). Instead of a dashboard of zeros, "held" rows and
 * "Simulated" data, the page shows one state: what is missing, what it
 * means for the office, and who fixes it. People who can set it up get Set
 * up and Open anyway; everyone else just the explanation (the sidebar
 * already leaves these out for them).
 *
 * Only pages with nothing to do until the service exists are listed.
 * Messages, Reminders (the Unconfirmed list works by phone), Online
 * booking and Insurance (a manual verification list) keep their pages with
 * one banner.
 */
import type { IntegrationKey } from '@velora/contracts/shell';

export interface GateCopy {
  key: IntegrationKey;
  /** "Texting needs A2P registration". */
  title: string;
  /** What is missing and what that means here. */
  missing: string;
  /** Who fixes it. */
  who: string;
}

export const GATED_ROUTES: Readonly<Record<string, GateCopy>> = {
  'campaigns:campaigns': {
    key: 'texting',
    title: 'Campaigns need texting, which is waiting on A2P registration',
    missing:
      'Carriers block texts from numbers that are not A2P 10DLC registered, so every campaign text would be held and nothing would reach patients.',
    who: 'Computers2Go is handling the registration. Campaigns open here once texting is live; the benefits campaign preview works now.',
  },
  // Not 'reviews:reviews' (Zac 2026-11-01): Review Requests is a module at
  // every office. Requests need texting and a Google review link, not the
  // Business Profile connection (that only feeds the review feed).
  'phones:phones': {
    key: 'phones',
    title: 'Phones needs the office phone system connected',
    missing:
      'Call logs, missed-call recovery and recordings come from 3CX or Stratus, which is not connected for these offices, so every count would read zero.',
    who: 'Computers2Go sets it up in Settings, Integrations, Phones.',
  },
  'receptionist:home': {
    key: 'receptionist',
    title: 'The AI Receptionist is not connected',
    missing:
      'After-hours calls are not answered by Velora until the receptionist service is linked to these offices.',
    who: 'Computers2Go sets it up.',
  },
};

export interface ActiveGate extends GateCopy {
  routeKey: string;
  setupHref: string;
  detail: string | null;
}

/** The gate for this route when its service reports `not_connected` (unknown never gates). */
export function gateFor(
  routeKey: string,
  integrations:
    | Partial<Record<IntegrationKey, { state: string; setupHref: string; detail?: string }>>
    | undefined,
): ActiveGate | null {
  const copy = GATED_ROUTES[routeKey];
  if (!copy) return null;
  const info = integrations?.[copy.key];
  if (info?.state !== 'not_connected') return null;
  return { ...copy, routeKey, setupHref: info.setupHref, detail: info.detail ?? null };
}
